import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Timezone utilities for EST consistency
export const TIMEZONE = process.env.REACT_APP_TIMEZONE || "America/New_York";

/**
 * Gets the current date/time in the configured timezone
 * @returns {Date} Current date in EST timezone
 */
export function getLocalDate() {
  try {
    const now = new Date();
    return new Date(now.toLocaleString("en-US", {timeZone: TIMEZONE}));
  } catch (error) {
    console.error('Timezone conversion failed, falling back to local time:', error);
    return new Date(); // Fallback to local time
  }
}

/**
 * Converts any date to EST date string in YYYY-MM-DD format
 * @param {string | Date} date - Date to convert
 * @returns {string} Date string in EST timezone YYYY-MM-DD format
 */
export function getLocalDateString(date) {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const estDate = new Date(dateObj.toLocaleString("en-US", {timeZone: TIMEZONE}));
    const year = estDate.getFullYear();
    const month = String(estDate.getMonth() + 1).padStart(2, '0');
    const day = String(estDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Date string conversion failed:', error);
    const fallback = new Date(date);
    const year = fallback.getFullYear();
    const month = String(fallback.getMonth() + 1).padStart(2, '0');
    const day = String(fallback.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

/**
 * Converts any date to EST datetime string
 * @param {string | Date} date - Date to convert
 * @returns {string} Formatted datetime in EST timezone
 */
export function getLocalDateTimeString(date) {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {timeZone: TIMEZONE});
  } catch (error) {
    console.error('DateTime string conversion failed:', error);
    return new Date(date).toLocaleString(); // Fallback
  }
}

/**
 * Converts any date to formatted EST date string
 * @param {string | Date} date - Date to convert
 * @returns {string} Formatted date with long month name in EST
 */
export function getLocalDateStringFormatted(date) {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Formatted date conversion failed:', error);
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }); // Fallback
  }
}

/**
 * Gets EST midnight for countdown calculations
 * @returns {Date} EST midnight time
 */
export function getLocalMidnight() {
  try {
    const estNow = getLocalDate();
    const midnight = new Date(estNow);
    midnight.setHours(24, 0, 0, 0);
    return midnight;
  } catch (error) {
    console.error('Midnight calculation failed:', error);
    // Fallback to local midnight
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    return midnight;
  }
}

/**
 * Gets today's date string in EST timezone
 * @returns {string} Today's date string in EST
 */
export function getLocalTodayString() {
  try {
    return getLocalDate().toDateString();
  } catch (error) {
    console.error('Today string conversion failed:', error);
    return new Date().toDateString(); // Fallback
  }
}

/**
 * Gets today's date string for form inputs in EST timezone
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getLocalTodayDateString() {
  try {
    const estDate = getLocalDate();
    const year = estDate.getFullYear();
    const month = String(estDate.getMonth() + 1).padStart(2, '0');
    const day = String(estDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Today date string conversion failed:', error);
    const fallback = new Date();
    const year = fallback.getFullYear();
    const month = String(fallback.getMonth() + 1).padStart(2, '0');
    const day = String(fallback.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}
